import React, { useRef } from "react";
import { useScroll, motion, useTransform } from "framer-motion";
import { ArrowRight, User, Monitor, Heart, Activity, Clipboard } from "react-feather";

const CARD_HEIGHT = 400;

// Define your service cards here
const CARDS = [
  {
    id: 1,
    Icon: Clipboard,
    title: "Nutritional Supplements",
    description: "Access a wide variety of nutritional supplements to support your health.",
    ctaClasses: "bg-green-300",
    routeTo: "/nutritional-supplements",
  },
  {
    id: 2,
    Icon: Activity,
    title: "Blood Pressure Check",
    description: "Get your blood pressure checked to monitor and maintain your health.",
    ctaClasses: "bg-blue-300",
    routeTo: "/blood-pressure-check",
  },
  {
    id: 3,
    Icon: Monitor,
    title: "Blood Sugar Check",
    description: "Regular blood sugar checks to keep your levels in control.",
    ctaClasses: "bg-red-300",
    routeTo: "/blood-sugar-check",
  },
  {
    id: 4,
    Icon: Heart,
    title: "Skin Care Advice",
    description: "Receive expert advice on maintaining healthy skin.",
    ctaClasses: "bg-yellow-300",
    routeTo: "/skin-care-advice",
  },
  {
    id: 5,
    Icon: User,
    title: "Medicational Counselling Services",
    description: "General counselling and addiction counselling services are offered.",
    ctaClasses: "bg-red-500",
    routeTo: "/counselling-services",
  },
];

export const Services = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });

  return (
    <section className="bg-gradient-to-br from-neutral-50 to-primary-50 py-20 w-full">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-neutral-900 mb-4">
              Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-600 to-secondary-600">Services</span>
            </h2>
            <p className="text-lg text-neutral-600 max-w-2xl mx-auto">
              Comprehensive healthcare services designed to keep you and your family healthy and well-informed
            </p>
          </motion.div>
        </div>
        <div ref={ref} className="relative w-full">
          {CARDS.map((card, index) => (
            <ServiceCard
              key={card.id}
              card={card}
              scrollYProgress={scrollYProgress}
              position={index + 1}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

const ServiceCard = ({ position, card, scrollYProgress }) => {
  const scaleFromPct = (position - 1) / CARDS.length;
  const y = useTransform(scrollYProgress, [scaleFromPct, 1], [0, -CARD_HEIGHT]);

  const isOddCard = position % 2;

  return (
    <motion.div
      style={{
        height: CARD_HEIGHT,
        y: position === CARDS.length ? undefined : y,
      }}
      className={`sticky top-0 flex w-full origin-top flex-col items-center justify-center px-4 ${
        isOddCard
          ? "bg-gradient-to-br from-neutral-900 to-neutral-800 text-white"
          : "bg-gradient-to-br from-white to-neutral-50 text-neutral-900"
      }`}
    >
      <div className={`w-20 h-20 rounded-2xl flex items-center justify-center mb-6 ${
        isOddCard
          ? "bg-white/10 backdrop-blur-sm"
          : "bg-primary-100"
      }`}>
        <card.Icon className={`text-3xl ${isOddCard ? "text-white" : "text-primary-600"}`} />
      </div>

      <h3 className="mb-6 text-center text-2xl md:text-4xl font-bold">
        {card.title}
      </h3>

      <p className={`mb-8 max-w-lg text-center text-base md:text-lg leading-relaxed ${
        isOddCard ? "text-neutral-300" : "text-neutral-600"
      }`}>
        {card.description}
      </p>

      <motion.a
        href={card.routeTo}
        className={`flex items-center gap-3 rounded-xl px-8 py-4 text-base font-semibold transition-all duration-300 ${
          isOddCard
            ? "bg-white text-neutral-900 hover:bg-neutral-100 shadow-large hover:shadow-xl"
            : "bg-gradient-to-r from-primary-600 to-primary-700 text-white hover:from-primary-700 hover:to-primary-800 shadow-medium hover:shadow-large"
        }`}
        whileHover={{ scale: 1.05, y: -2 }}
        whileTap={{ scale: 0.95 }}
      >
        <span>Learn More</span>
        <ArrowRight className="w-5 h-5" />
      </motion.a>
    </motion.div>
  );
};
